import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Eye, Download } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg, exportSubscriptionsCsv } from "@/lib/api";
import { PageHeader, Panel, Empty } from "@/components/common";

const EMPTY = { company_name: "", vat_number: "", contact_name: "", phone: "", address: "", pec: "", email: "", password: "", name: "" };

function NewClientDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const { email, password, name, ...client } = f;
    try {
      const { data } = await api.post("/admin/clients", { ...client, users: email ? [{ email, password, name }] : [] });
      toast.success("Cliente creato");
      setF(EMPTY);
      onCreated(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const F = (label, k, props = {}) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={f[k]} onChange={set(k)} data-testid={`new-client-${k}-input`} {...props} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[#111827] border-slate-800">
        <DialogHeader><DialogTitle>Nuovo cliente</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            {F("Ragione sociale *", "company_name", { required: true })}
            {F("Partita IVA", "vat_number")}
            {F("Referente", "contact_name")}
            {F("Telefono", "phone")}
            {F("Indirizzo", "address")}
            {F("PEC", "pec")}
          </div>
          <div className="pt-2 border-t border-slate-800">
            <div className="text-xs uppercase tracking-wider text-slate-500 my-3">Primo utente di accesso (facoltativo)</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {F("Email", "email", { type: "email" })}
              {F("Password", "password", { type: "text", minLength: 8, required: !!f.email })}
              {F("Nome", "name")}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-500" data-testid="new-client-submit-button">Crea cliente</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get("/admin/clients").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  const viewAs = (e, c) => {
    e.stopPropagation();
    navigate(`/admin/clienti/${c.id}/vista`);
  };
  const filtered = items.filter((c) => `${c.company_name} ${c.vat_number}`.toLowerCase().includes(q.toLowerCase()));
  const exportAll = () => exportSubscriptionsCsv(null).then(() => toast.success("CSV esportato")).catch((e) => toast.error(errMsg(e)));
  return (
    <div>
      <PageHeader eyebrow="Consolle amministrazione" title="Clienti" desc="Registra i clienti, i loro utenti di accesso, gli abbonamenti e i documenti."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportAll} className="border-slate-700 bg-transparent" data-testid="export-all-subscriptions-button"><Download size={16} className="mr-1" />Esporta tutti gli abbonamenti</Button>
          <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="add-client-modal-trigger"><Plus size={16} className="mr-1" />Nuovo cliente</Button>
        </div>} />
      <Panel testid="clients-table">
        <div className="p-4 border-b border-slate-800 relative">
          <Search size={15} className="absolute left-7 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Cerca per ragione sociale o P.IVA" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 max-w-sm" data-testid="clients-search-input" />
        </div>
        {filtered.length ? (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead>Ragione sociale</TableHead><TableHead>P.IVA</TableHead><TableHead className="text-right">Utenti</TableHead>
                <TableHead className="text-right">Abbonamenti</TableHead><TableHead className="text-right">Documenti</TableHead><TableHead>Stato</TableHead><TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} onClick={() => navigate(`/admin/clienti/${c.id}`)} className="cursor-pointer border-slate-800 hover:bg-slate-800/30" data-testid={`client-row-${c.id}`}>
                  <TableCell className="font-medium">{c.company_name}</TableCell>
                  <TableCell className="font-mono text-slate-400">{c.vat_number || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{c.users_count}</TableCell>
                  <TableCell className="text-right font-mono">{c.subscriptions_count}</TableCell>
                  <TableCell className="text-right font-mono">{c.documents_count}</TableCell>
                  <TableCell>{c.active ? <span className="text-emerald-400 text-xs">Attivo</span> : <span className="text-rose-400 text-xs">Disattivato</span>}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" title="Vedi come cliente" onClick={(e) => viewAs(e, c)} className="h-8 w-8 text-slate-400 hover:text-amber-300" data-testid={`view-as-client-${c.id}`}><Eye size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <Empty text="Nessun cliente trovato." />}
      </Panel>
      <NewClientDialog open={open} onOpenChange={setOpen} onCreated={(c) => { setOpen(false); navigate(`/admin/clienti/${c.id}`); }} />
    </div>
  );
}
